/**
 * PATH: packages/kanban-host/src/allocation/useKanbanQuickAllocate.ts
 * FILE_ID: AIHUB-KANBAN-ALLOC-QUICK-001
 * PURPOSE: One-call hook that powers the right-click menu's "Allocate
 *          to Kanban → Board / Lane" rows. Wraps the universal
 *          `KanbanApi.allocateEmailToLane` endpoint with the FE-side
 *          side-effects the operator expects: bump recents, invalidate
 *          the email list cache, surface a toast, and translate
 *          409-conflict errors into a human-readable line.
 *
 * WHY: Module B of EMAIL_KANBAN_ALLOCATION_PLAN.md. Centralising
 *      these side-effects (cache invalidation + recents bump + toast
 *      copy) means Module C (the row chip's "Re-allocate" dropdown)
 *      and any future call sites get them for free instead of
 *      duplicating the wiring.
 *
 * FLOW:
 *   allocate({ emailId, laneId, … })
 *     → KanbanApi.allocateEmailToLane()
 *        ├─ ok      → toast + invalidate ['emails', 'kanban-*'] + touchRecent
 *        └─ 409     → toast "On <Board · Lane>" + return conflict
 *
 * DEPENDENCIES:
 *   - @quotech/ui/kanban : the universal API + AllocationConflict type
 *   - @tanstack/react-query : QueryClient for invalidation
 *   - ./boardPrefs : touchRecentBoard
 *
 * RELATED:
 *   - apps/newmark/src/features/kanban/api.ts : getSamKanbanApi
 *   - apps/newmark/src/features/email/components/EmailContextMenu.tsx
 */

import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  KanbanAllocationConflictError,
  type AllocateEmailToLaneResult,
  type AllocationSource,
  type KanbanAllocationConflict,
  type KanbanApi,
} from '@quotech/ui/kanban'

import { touchRecentBoard } from './boardPrefs'
import {
  findRecentMirrorEvent,
  formatMirrorToastMessage,
} from './findRecentMirrorEvent'

// ──────────────────────────────────────────────────────────
// HARDEN-H6 — pure: classify an Error's HTTP status into a
// human-friendly message + retry hint.
//
// The underlying fetch helper throws `Error(message)` where
// `message` follows the pattern `"<METHOD> <path> failed: <STATUS>"`
// (see api.fetch.ts). We parse the status to differentiate:
//   - 401/403  → permission problem, retry won't help
//   - 412      → CAS conflict, refresh + retry helps
//   - 4xx      → validation, surface raw detail if present
//   - 5xx      → transient server, retry likely helps
//   - network  → no status (offline / aborted), retry likely helps
//
// Pre-fix every non-conflict error collapsed to
// "Could not allocate to Kanban." which hid 403s under the same
// label as network drops — operators with read-only access kept
// retrying forever because the message didn't say "permission".
// ──────────────────────────────────────────────────────────
export interface AllocateErrorClassification {
  /** Numeric HTTP status if parseable; null for network/abort errors. */
  status: number | null
  /** Human-friendly message for toast/inline. */
  message: string
  /** Whether a retry of the same call is likely to succeed. Powers
   *  the FE's "Retry" affordance — for 4xx perm errors we hide it. */
  retryable: boolean
}

export function classifyAllocateError(err: unknown): AllocateErrorClassification {
  const raw = (err as Error)?.message?.trim() || ''
  // Match the HTTP status across BOTH error-string shapes the kanban
  // fetch layer emits:
  //   • `_request`           → "<METHOD> <path> failed: 403"   (colon)
  //   • `allocateEmailToLane` → "Allocate failed (403): ..."   (paren)
  // The old `/failed:\s*(\d{3})/` only caught the first, so a 403 from
  // the allocate-email endpoint fell through to the network/unknown
  // branch and the operator never saw the actionable "no write access"
  // copy — the email silently failed to land on the board
  // (BUG-2026-06-21-EMAIL-ALLOCATE-403-MISCLASSIFIED). `\D*` skips any
  // non-digit separator (": ", " (") between "failed" and the status.
  const match = raw.match(/failed\D*(\d{3})/)
  const status = match ? Number(match[1]) : null

  if (status === 401 || status === 403) {
    return {
      status,
      message:
        "You don't have write access to this board. Ask the board owner to add you.",
      retryable: false,
    }
  }
  if (status === 412) {
    return {
      status,
      message:
        'Card was updated by another user. Refresh and try again.',
      retryable: true,
    }
  }
  if (status === 429) {
    return {
      status,
      message: 'Too many allocations at once — please retry in a moment.',
      retryable: true,
    }
  }
  if (status !== null && status >= 400 && status < 500) {
    return {
      status,
      message: raw || 'Could not allocate to Kanban (validation error).',
      retryable: false,
    }
  }
  if (status !== null && status >= 500) {
    return {
      status,
      message: 'Server error while allocating — retry in a moment.',
      retryable: true,
    }
  }
  // No parseable status → network / abort / unknown.
  const lower = raw.toLowerCase()
  if (lower.includes('abort') || lower.includes('cancel')) {
    return { status: null, message: 'Allocation cancelled.', retryable: true }
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return {
      status: null,
      message: 'Network issue — check your connection and retry.',
      retryable: true,
    }
  }
  return {
    status: null,
    message: raw || 'Could not allocate to Kanban.',
    retryable: true,
  }
}

export interface QuickAllocateInput {
  emailId: string
  laneId: string
  boardId: string
  /** Defaults to USER_MANUAL — the menu's primary use case. */
  source?: AllocationSource
  /** Optional confidence for AI-driven call sites. */
  confidence?: number | null
  /** Free-form classification snapshot for the org-brain corpus. */
  classificationSnapshot?: Record<string, unknown>
  /**
   * EK-14 — id of the prior allocation this call supersedes.
   *
   * Pass this on every operator override of an AI-suggested
   * allocation so the BE can revoke the prior + write a
   * `ClassificationCorrection` row capturing both lane + board
   * override. Without this, the silent-override path (operator
   * picks a different board via the right-click menu rather than
   * clicking the chip's cross) produces zero learning signal,
   * costing ~60% of the available training data — see
   * `email_kanban/services/allocation_learning.py` for the
   * corpus shape.
   *
   * Stale / cross-org IDs are silently ignored server-side, so
   * the FE can safely pass whatever it last saw without guarding.
   */
  supersedesAllocationId?: string | null
  /** Multi-placement (2026-07-09): create an ADDITIONAL card on the
   *  target lane instead of surfacing the "Already on…" conflict when
   *  the email is filed elsewhere. Powers the reader's "+ lane" chip. */
  allowAdditional?: boolean
}

export type QuickAllocateOutcome =
  | {
      kind: 'ok'
      result: AllocateEmailToLaneResult
    }
  | {
      kind: 'conflict'
      conflict: KanbanAllocationConflict
      message: string
      /** Arc B — true when the conflict was AUTO-RESOLVED by placing the
       *  existing card onto the requested lane (same card, one more live
       *  presence) instead of dead-ending on a toast. */
      resolved?: boolean
    }
  | {
      kind: 'error'
      message: string
      /** Optional HTTP status if the failure came from the server.
       *  Null for network/abort/unknown errors. */
      status?: number | null
      /** Whether retrying the same request is likely to succeed.
       *  Consumers can hide their Retry button when this is false. */
      retryable?: boolean
    }
  | {
      kind: 'busy'
      message: string
    }
  | {
      kind: 'batch'
      placed: number
      failed: number
      message: string
    }

export interface QuickAllocateManyInput {
  emailIds: string[]
  laneId: string
  boardId: string
  source?: AllocationSource
}

export interface QuickAllocateApi {
  busy: boolean
  /** Last error string surfaced to the caller — useful for inline
   *  re-render on a row chip that wants to flash a warning border. */
  error: string | null
  /** Reset transient error/busy state. */
  reset: () => void
  /** Run the allocation. Always resolves to a `QuickAllocateOutcome`
   *  so callers don't have to wrap in their own try/catch. */
  run: (input: QuickAllocateInput) => Promise<QuickAllocateOutcome>
  /** Same lane for every id. One summary toast, not one toast per email. */
  runMany: (input: QuickAllocateManyInput) => Promise<QuickAllocateOutcome>
}

// ──────────────────────────────────────────────────────────
// Pure: cache-key set we invalidate after a successful allocate.
//
// Centralised here so Module C's chip dropdown re-uses the EXACT
// same list (otherwise the row chip wouldn't update after a
// re-allocate even though the menu does). Add new keys here, not
// inline in every call site.
//
// `['kanban']` is a PREFIX match — react-query invalidates every
// query whose key starts with it: ['kanban','cards',boardId,laneId],
// ['kanban','email-rail',…], ['kanban','board',…],
// ['kanban','email-meta',…], ['kanban','boards',…] and friends.
// The previous list invalidated ['kanban-cards'] /
// ['kanban-allocations'] instead — hyphenated keys that match NO
// live query — so an open board stayed stale for up to 12s after a
// confirm/reject and only recovered via its version poll (UX audit
// 2026-06, Actions #1).
//
// ['kanban-boards'] / ['kanban-lanes'] stay: they ARE live keys —
// the allocation menu's board list (useAllocateToKanbanMenuData,
// AllocationChip prefetch) and lane lists (useKanbanBoardLanes).
// ──────────────────────────────────────────────────────────
const INVALIDATION_KEYS: ReadonlyArray<ReadonlyArray<string>> = [
  ['emails'],
  ['email-list'],
  ['kanban'],
  ['kanban-boards'],
  ['kanban-lanes'],
]

export function buildInvalidationKeys(): ReadonlyArray<ReadonlyArray<string>> {
  return INVALIDATION_KEYS
}

/**
 * Translate a structured conflict payload into the human-readable
 * line the toast renders. Pure I→O so we can unit-test it without
 * mocking the toast.
 */
export function describeConflict(conflict: KanbanAllocationConflict): string {
  return `Already on “${conflict.board_name} · ${conflict.lane_name}”. Open the card to move it.`
}

/**
 * Hook. The org-id is needed for the recents bump (per-tenant
 * namespace); when null we still allocate but skip the bump.
 */
export function useKanbanQuickAllocate(args: {
  api: KanbanApi
  orgId: number | null
  /** Optional toast bridge — tenants wire their own toaster. */
  notify?: (kind: 'success' | 'error' | 'info', message: string) => void
  /** Optional master Quotech user id. When provided, the post-
   *  allocate `touchRecentBoard` call writes to the per-(user, org)
   *  v2 recents key so two operators sharing a browser don't pollute
   *  each other's "Recent" bucket. Postmortem 2026-05-19 follow-up #6
   *  (qf!3196). When omitted, recents continue using the v1 per-org
   *  key (legacy contract). */
  currentUserId?: number | string | null
}): QuickAllocateApi {
  const { api, orgId, notify, currentUserId = null } = args
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // HARDEN-H5 — ref-based in-flight guard. The ``busy`` STATE is
  // exposed for the UI's spinner/disabled-button rendering, but
  // state updates batch + flush asynchronously, so two rapid
  // ``run()`` calls in the same tick can both see ``busy === false``
  // before either setState lands. A ref synchronously reflects the
  // in-flight status the moment we enter ``run`` and is the
  // authoritative gate. Without this, an operator who clicks a
  // menu row twice (or whose double-tap on touch fires two events)
  // sends two POSTs — under the legacy BE-side dedupe this
  // produced two cards on race conditions in the qb!2954 audit.
  const inFlightRef = useRef(false)

  const reset = useCallback(() => {
    setError(null)
    setBusy(false)
    inFlightRef.current = false
  }, [])

  const run = useCallback<QuickAllocateApi['run']>(
    async (input) => {
      // HARDEN-H5 — synchronous double-click guard. Must use the
      // ref, not the state, because state updates are async.
      if (inFlightRef.current) {
        const message = 'Allocation already in progress — please wait.'
        return { kind: 'busy', message }
      }
      inFlightRef.current = true
      setBusy(true)
      setError(null)
      try {
        const result = await api.allocateEmailToLane({
          emailId: input.emailId,
          laneId: input.laneId,
          source: input.source ?? 'USER_MANUAL',
          confidence: input.confidence ?? null,
          classificationSnapshot: input.classificationSnapshot,
          // EK-14 — pass through when set so the BE writes the
          // ClassificationCorrection row for the corpus.
          supersedesAllocationId: input.supersedesAllocationId ?? undefined,
          allowAdditional: input.allowAdditional || undefined,
        })

        // Side-effects: bump the recents pref, invalidate caches.
        // We don't await invalidations — they fan out reactively to
        // every mounted consumer and never throw.
        touchRecentBoard(orgId, input.boardId, currentUserId)
        for (const key of INVALIDATION_KEYS) {
          void queryClient.invalidateQueries({ queryKey: [...key] })
        }

        const message = result.created
          ? 'Email allocated to Kanban — opened a new card.'
          : 'Email allocated to Kanban — card already existed.'
        notify?.('success', message)

        // Newmark Stack 1 — surface the dual-board mirror to the
        // operator. The BE writes a `mirrored_to` audit-log entry
        // on the primary card when
        // `OrganisationSettings.email_kanban_dual_board_routing`
        // is on AND the classifier had a job destination. We read
        // that entry off the freshly returned card so a second
        // toast follows the primary "allocated" toast — operator
        // sees both the inbox landing AND the job-board mirror in
        // one breath. Pure FE: no new BE contract, just consumes
        // the audit_log_json the BE already serialises.
        const mirrorEntry = findRecentMirrorEvent(
          result.card as unknown as Parameters<typeof findRecentMirrorEvent>[0],
        )
        if (mirrorEntry) {
          notify?.('success', formatMirrorToastMessage(mirrorEntry))
        }

        return { kind: 'ok', result }
      } catch (err) {
        if (err instanceof KanbanAllocationConflictError) {
          // Arc B (email-card-boards unification) — the email already has
          // a card elsewhere. The old dead-end ("open the card to move
          // it") pushed operators toward allow_additional DUPLICATE
          // cards. The unified default: give the EXISTING card a live
          // placement on the requested lane — one card, one email, one
          // more presence. Falls back to the informational toast when
          // the host lacks the placements API or the placement fails
          // (e.g. no write access on the card's home board).
          if (api.addCardPlacement && !input.allowAdditional) {
            try {
              await api.addCardPlacement(err.conflict.card_id, {
                lane_id: input.laneId,
              })
              touchRecentBoard(orgId, input.boardId, currentUserId)
              for (const key of INVALIDATION_KEYS) {
                void queryClient.invalidateQueries({ queryKey: [...key] })
              }
              const message =
                `Already tracked on “${err.conflict.board_name} · ${err.conflict.lane_name}” — ` +
                'linked the same card here too (edits stay in sync).'
              notify?.('success', message)
              return {
                kind: 'conflict',
                conflict: err.conflict,
                message,
                resolved: true,
              }
            } catch {
              // fall through to the informational conflict toast below
            }
          }
          const message = describeConflict(err.conflict)
          setError(message)
          notify?.('info', message)
          return { kind: 'conflict', conflict: err.conflict, message }
        }
        // HARDEN-H6 — classify into HTTP-status-aware messages so
        // operators get actionable copy (permission vs network vs
        // transient server) instead of one opaque label.
        const classified = classifyAllocateError(err)
        setError(classified.message)
        notify?.('error', classified.message)
        return {
          kind: 'error',
          message: classified.message,
          status: classified.status,
          retryable: classified.retryable,
        }
      } finally {
        inFlightRef.current = false
        setBusy(false)
      }
    },
    [api, orgId, queryClient, notify, currentUserId],
  )

  const runMany = useCallback<QuickAllocateApi['runMany']>(
    async (input) => {
      if (inFlightRef.current) {
        const message = 'Allocation already in progress — please wait.'
        return { kind: 'busy', message }
      }
      inFlightRef.current = true
      setBusy(true)
      setError(null)
      let placed = 0
      let failed = 0
      try {
        for (const emailId of input.emailIds) {
          try {
            await api.allocateEmailToLane({
              emailId,
              laneId: input.laneId,
              source: input.source ?? 'USER_MANUAL',
            })
            placed += 1
          } catch (err) {
            if (err instanceof KanbanAllocationConflictError && api.addCardPlacement) {
              try {
                await api.addCardPlacement(err.conflict.card_id, { lane_id: input.laneId })
                placed += 1
                continue
              } catch {
                // Count as a failed place below.
              }
            }
            failed += 1
          }
        }
        touchRecentBoard(orgId, input.boardId, currentUserId)
        for (const key of INVALIDATION_KEYS) {
          void queryClient.invalidateQueries({ queryKey: [...key] })
        }
        const total = input.emailIds.length
        const message = failed === 0
          ? `Allocated ${placed} email${placed === 1 ? '' : 's'} to Kanban.`
          : `Allocated ${placed} of ${total} emails. ${failed} failed.`
        notify?.(failed === 0 ? 'success' : 'error', message)
        return { kind: 'batch', placed, failed, message }
      } finally {
        inFlightRef.current = false
        setBusy(false)
      }
    },
    [api, orgId, queryClient, notify, currentUserId],
  )

  return { busy, error, reset, run, runMany }
}
